﻿import { Link } from "react-router-dom";
import { hapticLight } from "../../utils/haptics.js";

/* -------------------------------------------------------------------------- */
/* Button — DESIGN.md §5                                                      */
/* Primary: gold gradient + on-primary.  Secondary: secondary-container.      */
/* Tertiary: no background, primary text, underline appears on hover only.    */
/* -------------------------------------------------------------------------- */

const VARIANTS = {
  primary:
    "gold-gradient text-on-primary shadow-gold-lg hover:scale-[1.02]",
  secondary:
    "bg-secondary-container text-on-secondary-container hover:bg-tertiary-fixed",
  tertiary:
    "bg-transparent text-primary hover:underline underline-offset-4 px-0",
  ghost:
    "bg-surface text-on-surface-variant hover:bg-surface-container",
  danger:
    "bg-error-container text-on-error-container hover:bg-error hover:text-on-error",
};

const SIZES = {
  sm: "px-4 py-2 text-xs",
  md: "px-6 py-3 text-sm",
  lg: "px-8 py-4 text-sm",
};

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap";

export default function Button({
  as,
  to,
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  className = "",
  children,
  disabled,
  onClick,
  ...rest
}) {
  const classes = `${BASE} ${SIZES[size] ?? SIZES.md} ${
    VARIANTS[variant] ?? VARIANTS.primary
  } ${className}`;

  /**
   * A light haptic tick on every deliberate press. Fires before the handler so
   * the confirmation lands with the tap, and is skipped when the button is
   * inert. No-ops on hardware without a vibration motor.
   */
  const handleClick = (event) => {
    if (!loading && !disabled) hapticLight();
    onClick?.(event);
  };

  const content = (
    <>
      {loading && (
        <span className="material-symbols-outlined animate-spin text-base">
          progress_activity
        </span>
      )}
      {!loading && icon && (
        <span className="material-symbols-outlined text-lg">{icon}</span>
      )}
      {children}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} onClick={handleClick} {...rest}>
        {content}
      </Link>
    );
  }

  const Component = as ?? "button";
  return (
    <Component
      className={classes}
      disabled={Component === "button" ? disabled || loading : undefined}
      aria-busy={loading || undefined}
      onClick={handleClick}
      {...rest}
    >
      {content}
    </Component>
  );
}
